import { useMemo } from "react";

import Avatar from "../user/Avatar";
import LoadingTable from "../shared/LoadingTable";
import Tooltip from "../shared/Tooltip";
import classNames from "../../utils/classNames";
import stringToColour from "../../utils/stringToColour";
import useWilmaUsers from "../../data/useWilmaUsers";
import { IEbayMessagingUserStat } from "../../models/EbayMessage";

const formatReplyTime = (minutes: number | null | undefined): string => {
  if (minutes === null || minutes === undefined) return "—";
  if (minutes < 1) return "< 1 min";
  if (minutes < 60) return `${Math.round(minutes)} min`;
  const hours = minutes / 60;
  if (hours < 24) return `${hours.toFixed(hours < 10 ? 1 : 0)} h`;
  const days = hours / 24;
  return `${days.toFixed(days < 10 ? 1 : 0)} d`;
};

const initialsFor = (name: string): string =>
  name
    .split(" ")
    .filter((word) => RegExp(/^\p{L}/, "u").test(word))
    .map((word, i) => (i < 2 ? word.substring(0, 1).toUpperCase() : ""))
    .join("") || "?";

interface EbayMessagingUserPerformanceProps {
  userStats?: IEbayMessagingUserStat[];
  isLoading: boolean;
  /** Tag the whole table as sample / placeholder data. */
  isSample?: boolean;
}

export default function EbayMessagingUserPerformance({
  userStats,
  isLoading,
  isSample,
}: EbayMessagingUserPerformanceProps) {
  const { wilmaUsers } = useWilmaUsers(true);

  const rows = useMemo(() => {
    if (!userStats) return [];
    // Fastest-responding / highest-volume users float to the top. Sort
    // by replies (desc), break ties on average time (asc).
    return [...userStats].sort((a, b) => {
      if (b.repliesSent !== a.repliesSent) {
        return b.repliesSent - a.repliesSent;
      }
      const aAvg = a.averageReplyMinutes ?? Number.POSITIVE_INFINITY;
      const bAvg = b.averageReplyMinutes ?? Number.POSITIVE_INFINITY;
      return aAvg - bAvg;
    });
  }, [userStats]);

  const nameFor = (row: IEbayMessagingUserStat): string => {
    if (row.displayName) return row.displayName;
    const found = wilmaUsers.data?.data?.find((u) => u.id === row.userId);
    if (found) return `${found.forename} ${found.surname}`.trim();
    return row.userId;
  };

  if (isLoading && !userStats) {
    return (
      <LoadingTable
        headers={["", "User", "Replies sent", "Avg. reply time"]}
        rows={4}
      />
    );
  }

  if (!rows.length) {
    return (
      <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-center text-sm text-slate-600">
        No replies recorded in this window yet.
      </div>
    );
  }

  const resolvesFromDirectory = (row: IEbayMessagingUserStat): boolean =>
    !!wilmaUsers.data?.data?.find((u) => u.id === row.userId);

  return (
    <div className="overflow-auto">
      {isSample && (
        <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-0.5 text-[11px] font-medium text-amber-700 ring-1 ring-inset ring-amber-200">
          Sample data — awaiting backend
        </div>
      )}
      <table className="min-w-full divide-y border divide-gray-300 bg-white">
        <thead className="bg-primary-50 text-left text-sm text-gray-800">
          <tr>
            <th scope="col" className="w-10 py-2.5 px-3" />
            <th scope="col" className="py-2.5 px-3 font-semibold">
              User
            </th>
            <th scope="col" className="py-2.5 px-3 font-semibold">
              Replies sent
            </th>
            <th scope="col" className="py-2.5 px-3 font-semibold">
              Avg. reply time
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 text-sm">
          {rows.map((row) => {
            const displayName = nameFor(row);
            const useDirectoryAvatar = resolvesFromDirectory(row);
            return (
              <tr
                key={row.userId}
                className={classNames(
                  "hover:bg-gray-50",
                  isSample ? "text-slate-600" : ""
                )}
              >
                <td className="px-3 py-2">
                  {useDirectoryAvatar ? (
                    <Avatar
                      userId={row.userId}
                      classes="w-8 h-8 rounded-full"
                      textClasses="text-sm"
                    />
                  ) : (
                    <Tooltip content={displayName}>
                      <span
                        className="inline-flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold text-white"
                        style={{ background: stringToColour(displayName) }}
                      >
                        {initialsFor(displayName)}
                      </span>
                    </Tooltip>
                  )}
                </td>
                <td className="px-3 py-2 font-medium text-gray-900">
                  {displayName}
                </td>
                <td className="px-3 py-2 text-gray-700">
                  {row.repliesSent.toLocaleString("en-GB")}
                </td>
                <td className="px-3 py-2 text-gray-700">
                  {formatReplyTime(row.averageReplyMinutes)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
